import { useCallback, useEffect, useRef, useState } from "react";
import { BarChart3, ClipboardCheck, Database, FlaskConical, Loader2, Plus, RefreshCw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { arenaApi } from "./stt-arena/data/api";
import type { ArenaConfig, Rankings, BenchmarkRun } from "./stt-arena/data/types";
import { RecentRuns } from "./stt-arena/recent-runs";
import { Leaderboard } from "./stt-arena/Leaderboard";
import { DataBrowser } from "./stt-arena/DataBrowser";
import { AddSample } from "./stt-arena/AddSample";
import { StudentLabeler } from "./stt-arena/StudentLabeler";
import { BenchmarkRuns } from "./stt-arena/BenchmarkRuns";
import "./stt-arena/SttArena.css";

export function SttArena() {
  const { user, userId, isAdmin } = useAuth();
  const [config, setConfig] = useState<ArenaConfig | null>(null);
  const [rankings, setRankings] = useState<Rankings | null>(null);
  const [tab, setTab] = useState("leaderboard");
  const [runs, setRuns] = useState<BenchmarkRun[]>([]);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [initialRunId, setInitialRunId] = useState<string | undefined>();
  const refreshing = useRef(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [offline, setOffline] = useState<string | null>(null);

  const loadRankings = useCallback(async () => {
    if (refreshing.current) return;
    refreshing.current = true;
    setIsRefreshing(true);
    try {
      await Promise.all([
        arenaApi.rankings().then((result) => { setRankings(result); setOffline(null); })
          .catch((e) => setOffline(String(e.message ?? e))),
        isAdmin ? arenaApi.benchmarkRuns().then((result) => {
          setRuns([...result.items].sort((a, b) => b.created_at.localeCompare(a.created_at)));
          setRunsError(null);
        }).catch((e) => setRunsError(String(e.message ?? e))) : Promise.resolve(),
      ]);
    } finally {
      refreshing.current = false;
      setIsRefreshing(false);
    }
  }, [isAdmin]);

  const loadConfig = useCallback(() => {
    void arenaApi.config().then((result) => { setConfig(result); setConfigError(null); })
      .catch((e) => setConfigError(String(e.message ?? e)));
  }, []);
  useEffect(loadConfig, [loadConfig]);

  useEffect(() => {
    if (tab !== "leaderboard") return;
    void loadRankings();
    const refresh = () => { if (document.visibilityState === "visible") void loadRankings(); };
    const timer = window.setInterval(refresh, 15_000);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [tab, loadRankings]);

  const openRuns = (runId?: string) => { setInitialRunId(runId); setTab("benchmarks"); };

  return (
    <div className="stt-arena">
      {configError && <p role="alert" className="mb-3 text-sm text-destructive">Arena configuration unavailable: {configError} <button className="stt-btn stt-btn--secondary" onClick={loadConfig}>Retry configuration</button></p>}
      <Tabs value={tab} onValueChange={setTab} className="stt-tabs">
        <TabsList className="stt-tabs-list" aria-label="STT Arena views">
          <TabsTrigger value="leaderboard"><BarChart3 size={15} /> Overview</TabsTrigger>
          <TabsTrigger value="data"><Database size={15} /> Browse data</TabsTrigger>
          <TabsTrigger value="student"><ClipboardCheck size={15} /> Label students</TabsTrigger>
          {isAdmin ? (
            <TabsTrigger value="benchmarks"><FlaskConical size={15} /> Benchmark runs</TabsTrigger>
          ) : null}
          <TabsTrigger value="add"><Plus size={15} /> Add sample</TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboard" className="stt-tab-content">
          <div className="mb-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>Refreshes every 15 seconds while visible.</span>
            <button className="stt-btn stt-btn--secondary" disabled={isRefreshing} aria-busy={isRefreshing} onClick={() => void loadRankings()}><RefreshCw size={14} /> {isRefreshing ? "Refreshing…" : "Refresh"}</button>
          </div>
          {isAdmin && <RecentRuns runs={runs} error={runsError} onSelect={openRuns} onNewRun={() => openRuns()} />}
          {offline && (
            <div role="alert" className="stt-empty stt-empty--err">
              <strong>{rankings ? "Showing previously loaded rankings." : "Rankings unavailable."}</strong>
              <p>{offline}</p>
              <button className="stt-btn stt-btn--secondary" disabled={isRefreshing} onClick={() => void loadRankings()}>{isRefreshing ? "Refreshing…" : "Retry rankings"}</button>
            </div>
          )}
          {rankings ? (
            <Leaderboard rankings={rankings} />
          ) : !offline ? (
            <div className="stt-loading">
              <Loader2 className="stt-spin" size={18} /> Loading rankings…
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="data" className="stt-tab-content">
          <DataBrowser config={config} />
        </TabsContent>

        <TabsContent value="student" className="stt-tab-content stt-student-tab-content">
          <StudentLabeler labeledBy={user ?? "unknown"} userId={userId ?? "unknown"} isManager={isAdmin} />
        </TabsContent>

        {isAdmin ? (
          <TabsContent value="benchmarks" className="stt-tab-content">
            <BenchmarkRuns initialRunId={initialRunId} />
          </TabsContent>
        ) : null}

        <TabsContent value="add" className="stt-tab-content">
          <AddSample config={config} createdBy={user ?? "unknown"} onBenchmarked={loadRankings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
